import { useCallback, useState } from "react";

type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

type ReturnKeys<T> = {
  (key: string, value: any): void;
  (key: keyof T, value: any): void;
};

export const useLoading = <KV>(
  initialValue?: KV | Record<string, any>
): [KV | Record<string, any>, ReturnKeys<KV>] => {
  const [loading, setLoading] = useState<KV | Record<string, any>>(
    initialValue
  );

  const updateLoading: ReturnKeys<KV> = useCallback(
    (key: keyof KV | string, value: boolean) => {
      setLoading((l: KV) => {
        return { ...l, [key]: value };
      });
    },
    []
  );

  return [loading, updateLoading];
};

export const useProgress = <KV>(
  initialValue?: KV | Record<string, any>
): [KV | Record<string, any>, ReturnKeys<KV>] => {
  const [progress, setProgress] = useState<KV | Record<string, number>>(
    initialValue
  );

  const updateProgress: ReturnKeys<KV> = useCallback(
    (key: keyof KV | string, value: number) => {
      setProgress((l: any) => {
        return { ...l, [key]: value };
      });
    },
    []
  );

  return [progress, updateProgress];
};

export const useDirtyText = (
  initialValue = {}
): [any, (update: any | string) => void, (key: string) => void] => {
  const [dirtyText, setDirtyText] = useState(initialValue);
  const updateDirtyText = useCallback((update: any | string) => {
    if (update === "all") {
      setDirtyText({});
    } else {
      setDirtyText((l: any) => {
        const keys = Object.keys(update);
        keys.forEach((key) => {
          l[key] = update[key];
        });

        return { ...l };
      });
    }
  }, []);

  const resetKey = useCallback((key: string) => {
    const newDirty = JSON.parse(JSON.stringify(dirtyText))
    delete newDirty[key];
    setDirtyText(newDirty)
  }, [setDirtyText, dirtyText])

  return [dirtyText, updateDirtyText, resetKey];
};
